import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getDocumentResult, type DocumentResultResponse } from '../api';

/** React Query key factory — keep all reviewer cache keys here for invalidation. */
export const reviewerQueryKeys = {
  all: ['reviewer'] as const,
  result: (jobId: string) => ['reviewer', 'result', jobId] as const,
  status: (jobId: string) => ['reviewer', 'status', jobId] as const,
  auditTrail: (jobId: string) => ['reviewer', 'audit-trail', jobId] as const,
  rawAi: (jobId: string) => ['reviewer', 'raw-ai', jobId] as const,
  children: (jobId: string) => ['reviewer', 'children', jobId] as const,
  comments: (jobId: string) => ['reviewer', 'comments', jobId] as const,
};

/**
 * Loads the terminal result of a document job. The query is `enabled` only when
 * `jobId` is non-empty — the reviewer page guards the call until the status
 * poll reports a terminal state.
 */
export function useDocumentResult(
  jobId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<DocumentResultResponse> {
  return useQuery({
    queryKey: reviewerQueryKeys.result(jobId),
    queryFn: () => getDocumentResult(jobId),
    enabled: (options.enabled ?? true) && jobId.length > 0,
    // The result is immutable once the job is terminal; cache aggressively.
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  });
}
